/**
 * Defensive pagination math shared by every server-paginated table. Pages are
 * 1-based. A page that is missing, non-numeric, fractional, zero or negative
 * parses as 1; a page beyond the last page clamps to the last page once the
 * total is known (an empty result has exactly one, empty, page).
 */
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

const toNumber = (raw: string | number | null | undefined): number =>
  typeof raw === 'number' ? raw : raw == null || raw.trim() === '' ? Number.NaN : Number(raw);

export function parsePage(raw: string | number | null | undefined): number {
  const n = toNumber(raw);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

export function parsePageSize(raw: string | number | null | undefined, fallback = DEFAULT_PAGE_SIZE): number {
  const n = toNumber(raw);
  if (!Number.isFinite(n) || n < 1) return fallback;
  return Math.min(MAX_PAGE_SIZE, Math.floor(n));
}

export function totalPages(total: number, pageSize: number): number {
  const size = parsePageSize(pageSize);
  return Math.max(1, Math.ceil(Math.max(0, total) / size));
}

export function clampPage(page: number, total: number, pageSize: number): number {
  return Math.min(parsePage(page), totalPages(total, pageSize));
}

/** 1-based inclusive row range shown as "from-to of total" ("0-0 of 0" when empty). */
export function pageRange(page: number, pageSize: number, total: number): { from: number; to: number } {
  const size = parsePageSize(pageSize);
  const safeTotal = Math.max(0, total);
  if (safeTotal === 0) return { from: 0, to: 0 };
  const p = clampPage(page, safeTotal, size);
  return { from: (p - 1) * size + 1, to: Math.min(p * size, safeTotal) };
}
